import type { LibraryFilter } from "@/lib/api/api-client";
import {
  LIBRARY_MINE_PATH,
  LIBRARY_RESUME_PATH,
  LIBRARY_SHARED_PATH,
  libraryMinePath,
} from "./library-routes";

/**
 * La rangée de pastilles de « Mes morceaux », et les comptes qui la nourrissent.
 *
 * # Une navigation, pas un réglage
 *
 * Chaque pastille est un **lien** vers une destination, miroir exact de
 * `LibraryFilter`. « En cours » et « Partagés » ont leur page, donc la pastille
 * y mène au lieu de filtrer sur place : sans ça, la même liste existerait à
 * deux endroits, avec deux tris à garder en phase et deux vides à écrire.
 * Aucun état hors de l'URL, donc rien à resynchroniser.
 *
 * # Pas de pastille « Du catalogue »
 *
 * Le serveur ne sait pas la servir, et un filtre que le navigateur calculerait
 * sur une page paginée mentirait sur son compte : il dirait « 6 » en n'ayant vu
 * que les vingt premières lignes.
 *
 * # Une pastille à zéro n'est pas rendue
 *
 * Sauf « Tout », qui est le retour, et sauf la pastille active : une pastille
 * qui disparaîtrait sous le doigt qui vient de l'ouvrir laisserait sur une page
 * sans chemin de retour visible.
 *
 * Module pur : ni React, ni DOM. Testé dans `__tests__/library-views.test.ts`.
 */

/**
 * Les quatre comptes qu'une bibliothèque affiche, et le filtre d'API qui les
 * rend.
 *
 * `practiced` et non `in-progress` : « En cours » mène à « Là où tu t'es
 * arrêté », qui montre les trois états de travail. Compter les seuls
 * `in_progress` afficherait « En cours 3 » au-dessus d'une page qui en montre
 * sept.
 */
export type LibraryCountKey = "all" | "practiced" | "imports" | "shared";

export const LIBRARY_COUNT_FILTERS: Record<LibraryCountKey, LibraryFilter> = {
  all: "all",
  practiced: "practiced",
  imports: "imports",
  shared: "shared",
};

export const LIBRARY_COUNT_KEYS: readonly LibraryCountKey[] = [
  "all",
  "practiced",
  "imports",
  "shared",
];

export type LibraryCounts = Readonly<Record<LibraryCountKey, number>>;

/** Ce qu'on affiche tant que rien n'est chargé, et pour un visiteur anonyme. */
export const EMPTY_LIBRARY_COUNTS: LibraryCounts = {
  all: 0,
  practiced: 0,
  imports: 0,
  shared: 0,
};

export type LibraryChip = {
  readonly key: LibraryCountKey;
  readonly label: string;
  readonly href: string;
  readonly count: number;
  readonly active: boolean;
};

const CHIPS: readonly {
  key: LibraryCountKey;
  label: string;
  href: string;
}[] = [
  { key: "all", label: "Tout", href: LIBRARY_MINE_PATH },
  { key: "practiced", label: "En cours", href: LIBRARY_RESUME_PATH },
  { key: "imports", label: "Mes imports", href: libraryMinePath("imports") },
  { key: "shared", label: "Partagés", href: LIBRARY_SHARED_PATH },
];

export function libraryChips(
  counts: LibraryCounts,
  current: LibraryCountKey,
): readonly LibraryChip[] {
  return CHIPS.filter(
    (chip) =>
      chip.key === "all" || chip.key === current || counts[chip.key] > 0,
  ).map((chip) => ({ ...chip, count: counts[chip.key], active: chip.key === current }));
}
